import React, {useState, useEffect} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  Button as ButtonMui,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {useLazyQuery, useMutation} from "@apollo/client";
import {QUERY_CONTENT_TYPE, GOLF_ADD_ATTACHMENT, GOLF_GET_ATTACHMENT, GOLF_DELETE_ATTACHMENT} from "../../../utilis/utils.js";
import AlertDialog from "../../common/AlertDialog.jsx";
import ConfirmDialog from "../../common/ConfirmDialog.jsx";
import {colors} from "../../../utilis/theme.js";

const TIPI_DOCUMENTO = [
  "Carta d'identità",
  "Patente di guida",
  "Contratto di lavoro",
  "Certificato medico",
  "Attestato di formazione",
  "Documento di sicurezza",
  "Permesso di soggiorno",
  "Altro",
];

export default function DocumentDialog({open, onClose, dipendente}) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [nomeDoc, setNomeDoc] = useState("");
  const [tipoDoc, setTipoDoc] = useState("");
  const [fileSelezionato, setFileSelezionato] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState({open: false, message: "", type: "success"});
  const [confirm, setConfirm] = useState({open: false, id: null});
  const [contentTypeId, setContentTypeId] = useState(null);
  const [documenti, setDocumenti] = useState([]);

  const [queryContentType] = useLazyQuery(QUERY_CONTENT_TYPE, {
    onCompleted: (data) => {
      console.log("[DocumentDialog] ContentType query result:", data);
      const ct = data?.django_content_type?.[0];
      if (ct) {
        console.log("[DocumentDialog] ContentType ID:", ct.id, "for dipendente.id:", dipendente.id);
        setContentTypeId(ct.id);
        queryGetAttachments({variables: {contentTypeId: ct.id, objectId: dipendente.id}});
      } else {
        console.warn("[DocumentDialog] ContentType non trovato per 'usergolf'");
      }
    },
    onError: (error) => {
      console.error("[DocumentDialog] Errore query ContentType:", error);
    },
  });

  const [queryGetAttachments, {loading: loadingDocs}] = useLazyQuery(GOLF_GET_ATTACHMENT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("[DocumentDialog] GetAttachments result:", data);
      const key = Object.keys(data).find((k) => k.endsWith("_attachments_attachment"));
      console.log("[DocumentDialog] Documenti trovati:", data[key]?.length || 0);
      setDocumenti(data[key] || []);
    },
    onError: (error) => {
      console.error("[DocumentDialog] Errore query GetAttachments:", error);
    },
  });

  const [addAttachment] = useMutation(GOLF_ADD_ATTACHMENT);
  const [deleteAttachment] = useMutation(GOLF_DELETE_ATTACHMENT);

  useEffect(() => {
    if (open && dipendente) {
      queryContentType({variables: {model: "usergolf"}});
    }
  }, [open, dipendente]);

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    console.log("[DocumentDialog] File selezionato:", file.name, "tipo:", file.type, "size:", file.size);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      console.log("[DocumentDialog] File letto come base64, lunghezza content:", reader.result?.length);
      setFileSelezionato({
        name: file.name,
        type: file.type,
        content: reader.result,
        size: file.size,
      });
    };
    reader.onerror = (err) => {
      console.error("[DocumentDialog] Errore lettura file:", err);
    };
  }

  async function handleUpload() {
    if (!nomeDoc || !tipoDoc || !fileSelezionato) return;
    if (!contentTypeId) {
      console.error("[DocumentDialog] Upload bloccato: contentTypeId è null! La query ContentType non ha trovato 'usergolf'.");
      setAlert({open: true, message: "Errore: content type 'usergolf' non trovato. Verificare configurazione Hasura.", type: "error"});
      return;
    }
    setUploading(true);
    const variables = {
      name: nomeDoc,
      contentType: contentTypeId,
      objectId: dipendente.id,
      attachment: JSON.stringify(fileSelezionato),
      tipoDocumento: tipoDoc,
    };
    console.log("[DocumentDialog] Upload - variables:", {
      ...variables,
      attachment: variables.attachment.substring(0, 100) + "... (troncato)",
    });
    try {
      const result = await addAttachment({ variables });
      console.log("[DocumentDialog] Upload - risultato mutation:", result);
      setAlert({open: true, message: "Documento caricato con successo", type: "success"});
      setAddDialogOpen(false);
      resetForm();
      queryGetAttachments({variables: {contentTypeId, objectId: dipendente.id}});
    } catch (error) {
      console.error("[DocumentDialog] Upload - ERRORE:", error);
      console.error("[DocumentDialog] Upload - graphQLErrors:", error.graphQLErrors);
      console.error("[DocumentDialog] Upload - networkError:", error.networkError);
      if (error.networkError?.result) {
        console.error("[DocumentDialog] Upload - networkError result:", error.networkError.result);
      }
      setAlert({open: true, message: "Errore durante il caricamento", type: "error"});
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id) {
    console.log("[DocumentDialog] Delete - attachment id:", id);
    try {
      const result = await deleteAttachment({variables: {id}});
      console.log("[DocumentDialog] Delete - risultato:", result);
      setDocumenti((prev) => prev.filter((d) => d.id !== id));
      setAlert({open: true, message: "Documento eliminato", type: "success"});
    } catch (error) {
      console.error("[DocumentDialog] Delete - ERRORE:", error);
      setAlert({open: true, message: "Errore durante l'eliminazione", type: "error"});
    }
  }

  function resetForm() {
    setNomeDoc("");
    setTipoDoc("");
    setFileSelezionato(null);
  }

  function handleCloseAdd() {
    setAddDialogOpen(false);
    resetForm();
  }

  const formValid = nomeDoc && tipoDoc && fileSelezionato;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{sx: {borderRadius: "12px"}}}>
        <DialogTitle sx={{fontWeight: 600, color: colors.textPrimary}}>
          Documenti — {dipendente?.name} {dipendente?.surname}
        </DialogTitle>
        <DialogContent dividers>
          {loadingDocs ? (
            <div style={{textAlign: "center", padding: "24px"}}>
              <CircularProgress size={32} />
            </div>
          ) : documenti.length === 0 ? (
            <Typography color={colors.textSecondary} sx={{textAlign: "center", py: 3}}>
              Nessun documento presente
            </Typography>
          ) : (
            <List dense>
              {documenti.map((doc, i) => (
                <React.Fragment key={doc.id}>
                  {i > 0 && <Divider />}
                  <ListItem sx={{py: 1.5}}>
                    <ListItemText
                      primary={<Typography fontWeight={500}>{doc.name}</Typography>}
                      secondary={
                        <>
                          {doc.tipo_documento && <span style={{color: colors.primary}}>{doc.tipo_documento}</span>}
                          {doc.created_at && (
                            <span style={{color: colors.textSecondary, marginLeft: 8}}>
                              {new Date(doc.created_at).toLocaleDateString("it-IT")}
                            </span>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Scarica">
                        <IconButton edge="end" onClick={() => window.open("/media/" + doc.attachment)} sx={{mr: 0.5}}>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Elimina">
                        <IconButton edge="end" color="error" onClick={() => setConfirm({open: true, id: doc.id})}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{px: 3, py: 2, justifyContent: "space-between"}}>
          <ButtonMui
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{backgroundColor: colors.primary, textTransform: "none"}}
          >
            Aggiungi documento
          </ButtonMui>
          <ButtonMui variant="outlined" onClick={onClose} sx={{textTransform: "none"}}>
            Chiudi
          </ButtonMui>
        </DialogActions>
      </Dialog>

      {/* Sub-dialog: aggiungi documento */}
      <Dialog open={addDialogOpen} onClose={handleCloseAdd} maxWidth="xs" fullWidth PaperProps={{sx: {borderRadius: "12px"}}}>
        <DialogTitle sx={{fontWeight: 600}}>Aggiungi documento</DialogTitle>
        <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, pt: "16px !important"}}>
          <TextField
            label="Nome documento"
            value={nomeDoc}
            onChange={(e) => setNomeDoc(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            select
            label="Tipo documento"
            value={tipoDoc}
            onChange={(e) => setTipoDoc(e.target.value)}
            required
            fullWidth
            size="small"
          >
            {TIPI_DOCUMENTO.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <ButtonMui variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{textTransform: "none"}}>
            {fileSelezionato ? fileSelezionato.name : "Seleziona file"}
            <input type="file" hidden onChange={handleFileChange} />
          </ButtonMui>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 3, gap: 1}}>
          <ButtonMui variant="outlined" onClick={handleCloseAdd} sx={{textTransform: "none"}}>
            Annulla
          </ButtonMui>
          <ButtonMui
            variant="contained"
            onClick={handleUpload}
            disabled={!formValid || uploading}
            sx={{backgroundColor: colors.primary, textTransform: "none", minWidth: 100}}
          >
            {uploading ? <CircularProgress size={20} color="inherit" /> : "Carica"}
          </ButtonMui>
        </DialogActions>
      </Dialog>

      <AlertDialog open={alert.open} onClose={() => setAlert((a) => ({...a, open: false}))} message={alert.message} type={alert.type} />
      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({open: false, id: null})}
        onConfirm={() => handleDelete(confirm.id)}
        message="Sei sicuro di voler eliminare questo documento? L'operazione è irreversibile."
        type="delete"
        confirmLabel="Elimina"
      />
    </>
  );
}
